import { db } from '../../firebase/firebase';
import { doc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { hasRole } from '../../utilities/roleUtils';
import { encryptEmail } from '../../utilities/emailEncryption';
import {
  addRoleToOrganisation,
  hasRoleInAnyOrganisation,
  removeRoleFromOrganisation,
  removeRoleFromOrganisations,
  resolveFacilitatorInfo,
  resolveFacilitatorProfileCompleteness,
  FacilitatorInfo,
  FacilitatorProfileCompleteness,
  UserOrganisations,
} from './userOrganisations';
import { OVERHEAR_ORGANISATION_ID } from '../../constants/organisations';

/**
 * Facilitator / wanderWriter / org-role mutations and checks, extracted from
 * UserManager. All functions talk to Firestore directly and mirror the
 * original UserManager method bodies exactly.
 */

/**
 * Updates a user's facilitator status. If the user is not already a facilitator,
 * it adds the facilitator role to a default/global organisation. If the user is
 * already a facilitator, it removes the facilitator role.
 * @param userId The user ID to update
 * @param isFacilitator Whether the user should be a facilitator
 * @returns A Promise that resolves to void if successful, or rejects with an error
 */
export const updateFacilitatorStatus = async (userId: string, isFacilitator: boolean): Promise<void> => {
  try {
    const userDocRef = doc(db, 'Users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const organisations = userData.Organisations || {};

    // Default organisation ID for facilitators (you may need to create this organisation first)
    const facilitatorOrgId = 'facilitator-organisation';

    if (isFacilitator) {
      // Add facilitator role to organisation if not already present
      if (!organisations[facilitatorOrgId] || !hasRole(organisations[facilitatorOrgId]?.roles, 'facilitator')) {
        // Encrypt email before storing
        const emailToEncrypt = userData.email || '';
        const encryptedEmail = emailToEncrypt ? await encryptEmail(emailToEncrypt.trim().toLowerCase()) : '';

        const updatedOrgs = {
          ...organisations,
          [facilitatorOrgId]: {
            userKey: userId,
            organisationId: facilitatorOrgId,
            roles: ['facilitator'],
            email: encryptedEmail,
            name: userData.displayName || '',
            joinedAt: Timestamp.now(),
            groupIds: []
          }
        };

        await updateDoc(userDocRef, {
          Organisations: updatedOrgs,
          updatedAt: Timestamp.now()
        });
      }
    } else {
      await updateDoc(userDocRef, {
        Organisations: removeRoleFromOrganisations(organisations, 'facilitator'),
        updatedAt: Timestamp.now()
      });
    }
  } catch (error) {
    console.error("Error updating facilitator status:", error);
    throw error;
  }
};

/**
 * @deprecated Use updateWanderWriterStatusForOrganisation instead.
 * This method is kept for backward compatibility but should not be used for new implementations.
 * Wander writers should be stored in the Organisations field with the 'wanderWriter' role.
 */
export const updateWanderWriterStatus = async (userId: string, isWanderWriter: boolean): Promise<void> => {
  try {
    const userDocRef = doc(db, 'Users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    await updateDoc(userDocRef, {
      isWanderWriter: isWanderWriter,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error("Error updating wander writer status:", error);
    throw error;
  }
};

/**
 * Updates a user's wander writer status for a specific organisation.
 * @param userId The user ID to update
 * @param isWanderWriter Whether the user should be a wander writer
 * @param organisationId The organisation ID to assign the wander writer role to (required when setting to wander writer)
 * @returns A Promise that resolves to void if successful, or rejects with an error
 */
export const updateWanderWriterStatusForOrganisation = async (userId: string, isWanderWriter: boolean, organisationId?: string): Promise<void> => {
  try {
    const userDocRef = doc(db, 'Users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const organisations = userData.Organisations || {};

    if (isWanderWriter) {
      if (!organisationId) {
        throw new Error('Organisation ID is required when setting user as wander writer');
      }

      // Check if organisation exists
      const orgDocRef = doc(db, 'Organisations', organisationId);
      const orgDoc = await getDoc(orgDocRef);

      if (!orgDoc.exists()) {
        throw new Error('Organisation not found');
      }

      // Add wander writer role to the specified organisation
      const existingOrg = organisations[organisationId];
      const existingRoles = existingOrg?.roles || [];

      // If user already has roles in this organisation, add wanderWriter to the array
      // Otherwise, create a new organisation entry
      const updatedOrgs = {
        ...organisations,
        [organisationId]: {
          userKey: userId,
          organisationId: organisationId,
          roles: existingRoles.includes('wanderWriter')
            ? existingRoles
            : [...existingRoles, 'wanderWriter'],
          email: userData.email || '',
          name: userData.userProfile?.userName || userData.displayName || '',
          joinedAt: existingOrg?.joinedAt || Timestamp.now(),
          groupIds: existingOrg?.groupIds || []
        }
      };

      await updateDoc(userDocRef, {
        Organisations: updatedOrgs,
        updatedAt: Timestamp.now()
      });
    } else {
      // Remove the wander writer role, dropping organisations left with no roles
      await updateDoc(userDocRef, {
        Organisations: removeRoleFromOrganisations(organisations, 'wanderWriter'),
        updatedAt: Timestamp.now()
      });
    }
  } catch (error) {
    console.error("Error updating wander writer status for organisation:", error);
    throw error;
  }
};

/**
 * Checks if a user has wander writer status in any organisation
 * @param userId The user ID to check
 * @returns A Promise that resolves to boolean indicating wander writer status
 */
export const isWanderWriter = async (userId: string): Promise<boolean> => {
  try {
    const userDocRef = doc(db, 'Users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      return false;
    }

    const userData = userDoc.data();
    return hasRoleInAnyOrganisation(userData.Organisations || {}, 'wanderWriter');
  } catch (error) {
    console.error("Error checking wander writer status:", error);
    return false;
  }
};

/**
 * Updates a user's facilitator status for a specific organisation.
 * @param userId The user ID to update
 * @param isFacilitator Whether the user should be a facilitator
 * @param organisationId The organisation ID to assign the facilitator role to (required when setting to facilitator)
 * @returns A Promise that resolves to void if successful, or rejects with an error
 */
export const updateFacilitatorStatusForOrganisation = async (userId: string, isFacilitator: boolean, organisationId?: string): Promise<void> => {
  try {
    const userDocRef = doc(db, 'Users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data();
    const organisations = userData.Organisations || {};

    if (isFacilitator) {
      if (!organisationId) {
        throw new Error('Organisation ID is required when setting user as facilitator');
      }

      // Check if organisation exists
      const orgDocRef = doc(db, 'Organisations', organisationId);
      const orgDoc = await getDoc(orgDocRef);

      if (!orgDoc.exists()) {
        throw new Error('Organisation not found');
      }

      const existingOrg = organisations[organisationId];
      const updatedOrgs = addRoleToOrganisation(
        {
          ...organisations,
          [organisationId]: {
            ...existingOrg,
            userKey: userId,
            organisationId,
            email:
              existingOrg?.email ||
              (userData.email ? await encryptEmail(userData.email.trim().toLowerCase()) : ''),
            name:
              existingOrg?.name ||
              userData.userProfile?.userName ||
              userData.displayName ||
              '',
            joinedAt: existingOrg?.joinedAt || Timestamp.now(),
            groupIds: existingOrg?.groupIds || [],
          },
        },
        organisationId,
        'facilitator'
      );

      await updateDoc(userDocRef, {
        Organisations: updatedOrgs,
        updatedAt: Timestamp.now()
      });
    } else {
      await updateDoc(userDocRef, {
        Organisations: removeRoleFromOrganisations(organisations, 'facilitator'),
        updatedAt: Timestamp.now()
      });
    }
  } catch (error) {
    console.error("Error updating facilitator status for organisation:", error);
    throw error;
  }
};

/**
 * Checks if a user has facilitator status in any organisation
 * @param userId The user ID to check
 * @returns A Promise that resolves to boolean indicating facilitator status
 */
export const isFacilitator = async (userId: string): Promise<boolean> => {
  try {
    const userDocRef = doc(db, 'Users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      return false;
    }

    const userData = userDoc.data();
    return hasRoleInAnyOrganisation(userData.Organisations || {}, 'facilitator');
  } catch (error) {
    console.error("Error checking facilitator status:", error);
    return false;
  }
};

/**
 * Gets facilitator compliance information for a user
 * Checks Organisations[orgId].facilitatorInfo first, then falls back to root facilitatorInfo
 * @param userId The user ID to get facilitator info for
 * @returns A Promise that resolves to facilitator info or null if user not found
 */
export const getFacilitatorInfo = async (userId: string): Promise<FacilitatorInfo | null> => {
  try {
    const userDocRef = doc(db, 'Users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      return null;
    }

    return resolveFacilitatorInfo(userDoc.data());
  } catch (error) {
    console.error("Error getting facilitator info:", error);
    throw error;
  }
};

/**
 * Checks if a facilitator's profile is complete (has email and name)
 * Checks Organisations[orgId].email field (encrypted) instead of Users.email
 * @param userId The user ID to check
 * @returns A Promise that resolves to profile completion status and data
 */
export const checkFacilitatorProfileCompleteness = async (userId: string): Promise<FacilitatorProfileCompleteness> => {
  try {
    const userDocRef = doc(db, 'Users', userId);
    const userDoc = await getDoc(userDocRef);

    if (!userDoc.exists()) {
      return {
        isComplete: false,
        email: '',
        name: '',
        userExists: false
      };
    }

    return resolveFacilitatorProfileCompleteness(userDoc.data());
  } catch (error) {
    console.error("Error checking facilitator profile completeness:", error);
    return {
      isComplete: false,
      email: '',
      name: '',
      userExists: false
    };
  }
};

/**
 * Adds or removes `organisationAdmin` on the OVERHEAR organisation membership
 * without dropping other roles (e.g. facilitator) or facilitatorInfo.
 */
export const syncOverhearOrganisationAdminRole = async (
  userId: string,
  userData: {
    email?: string;
    displayName?: string;
    Organisations?: UserOrganisations;
    userProfile?: { userName?: string };
  },
  isOrgAdmin: boolean
): Promise<void> => {
  const organisations = userData.Organisations || {};
  const updated = isOrgAdmin
    ? addRoleToOrganisation(organisations, OVERHEAR_ORGANISATION_ID, 'organisationAdmin', {
        userKey: userId,
        organisationId: OVERHEAR_ORGANISATION_ID,
        email: userData.email || '',
        name: userData.userProfile?.userName || userData.displayName || '',
        joinedAt: Timestamp.now(),
        groupIds: [],
      })
    : removeRoleFromOrganisation(organisations, OVERHEAR_ORGANISATION_ID, 'organisationAdmin');

  if (updated === organisations) return;

  await updateDoc(doc(db, 'Users', userId), {
    Organisations: updated,
    updatedAt: Timestamp.now(),
  });
};
